import { useCallback, useEffect, useState } from 'react';
import type { Instrument, QCSample, QCRun } from './qcUtils';

const KEYS = {
    instruments: 'lab-qc-instruments',
    samples: 'lab-qc-samples',
    runs: 'lab-qc-runs',
};

function readList<T>(key: string): T[] {
    try {
        const raw = window.localStorage.getItem(key);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

function writeList<T>(key: string, list: T[]) {
    try { window.localStorage.setItem(key, JSON.stringify(list)); } catch { /* storage unavailable */ }
}

const uid = (prefix: string) => `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;

export function useQCData() {
    const [instruments, setInstruments] = useState<Instrument[]>(() => readList<Instrument>(KEYS.instruments));
    const [samples, setSamples] = useState<QCSample[]>(() => readList<QCSample>(KEYS.samples));
    const [runs, setRuns] = useState<QCRun[]>(() => readList<QCRun>(KEYS.runs));

    useEffect(() => { writeList(KEYS.instruments, instruments); }, [instruments]);
    useEffect(() => { writeList(KEYS.samples, samples); }, [samples]);
    useEffect(() => { writeList(KEYS.runs, runs); }, [runs]);

    const addInstrument = useCallback((data: Omit<Instrument, 'id' | 'createdAt'>) => {
        const instrument: Instrument = { ...data, id: uid('inst'), createdAt: new Date().toISOString() };
        setInstruments(prev => [instrument, ...prev]);
        return instrument;
    }, []);

    const deleteInstrument = useCallback((id: string) => {
        setInstruments(prev => prev.filter(i => i.id !== id));
        setSamples(prev => prev.filter(s => s.instrumentId !== id));
    }, []);

    const addQCSample = useCallback((data: Omit<QCSample, 'id' | 'createdAt'>) => {
        const sample: QCSample = { ...data, id: uid('qcs'), createdAt: new Date().toISOString() };
        setSamples(prev => [sample, ...prev]);
        return sample;
    }, []);

    const deleteQCSample = useCallback((id: string) => {
        setSamples(prev => prev.filter(s => s.id !== id));
        setRuns(prev => prev.filter(r => r.qcSampleId !== id));
    }, []);

    const addRun = useCallback((data: Omit<QCRun, 'id'>) => {
        const run: QCRun = { ...data, id: uid('run') };
        setRuns(prev => [...prev, run]);
        return run;
    }, []);

    const deleteRun = useCallback((id: string) => {
        setRuns(prev => prev.filter(r => r.id !== id));
    }, []);

    return {
        instruments, addInstrument, deleteInstrument,
        samples, addQCSample, deleteQCSample,
        runs, addRun, deleteRun,
    };
}
